import { HttpTypes } from "@medusajs/types"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"

export const COLLECTION_SORT_OPTIONS: { label: string; value: SortOptions }[] = [
  { label: "Latest Arrivals", value: "created_at" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
]

/**
 * Data-driven filter facets for collection pages.
 *
 * Sizes are NEVER hard-coded — they are derived from the union of every Size
 * option value across ALL products in the collection, then ordered naturally
 * (lettered sizes by garment order, numeric/age sizes by their leading number).
 */

export type CollectionFacets = {
  sizes: string[]
  priceMin: number
  priceMax: number
}

export type CollectionFilters = {
  sizes: string[]
  minPrice?: number
  maxPrice?: number
}

// Canonical garment-size order. Anything not listed falls back to numeric /
// alphabetic comparison so age ranges ("0/1", "2/3") and waist sizes ("30",
// "32") still sort sensibly.
const SIZE_ORDER = [
  "xxxs",
  "xxs",
  "xs",
  "s",
  "small",
  "m",
  "medium",
  "l",
  "large",
  "xl",
  "xxl",
  "2xl",
  "xxxl",
  "3xl",
  "xxxxl",
  "4xl",
]

const normalize = (value: string) => value.trim().toLowerCase()

const leadingNumber = (value: string): number | null => {
  const match = value.match(/\d+(\.\d+)?/)
  return match ? parseFloat(match[0]) : null
}

export const compareSizes = (a: string, b: string): number => {
  const na = normalize(a)
  const nb = normalize(b)
  const ia = SIZE_ORDER.indexOf(na)
  const ib = SIZE_ORDER.indexOf(nb)

  // Both are known lettered sizes → use the canonical order.
  if (ia !== -1 && ib !== -1) return ia - ib
  // Only one is a known lettered size → lettered sizes sort before ad-hoc ones.
  if (ia !== -1) return -1
  if (ib !== -1) return 1

  // Neither is a known token → numeric leading value, then alphabetic.
  const da = leadingNumber(a)
  const db = leadingNumber(b)
  if (da !== null && db !== null && da !== db) return da - db
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
}

const isSizeOption = (title?: string | null) =>
  String(title || "")
    .toLowerCase()
    .includes("size")

/**
 * URL-safe token for a size value. Size labels contain slashes ("2/3", "0/1"),
 * which become `%2F` in a query string and break Next.js App Router CLIENT
 * navigation (the soft nav fetches the RSC but never commits the URL). We carry
 * a slug in the URL instead and match products by comparing slugs, so the raw
 * label never has to round-trip. Idempotent: sizeToParam(slug) === slug.
 */
export const sizeToParam = (size: string): string =>
  size
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

/** Every Size value a single product is offered in. */
export const getProductSizeValues = (
  product: HttpTypes.StoreProduct
): string[] => {
  const sizeOption = (product.options || []).find((o) => isSizeOption(o.title))
  if (!sizeOption) return []
  return (sizeOption.values || [])
    .map((v) => (v.value || "").trim())
    .filter(Boolean)
}

/** Lowest calculated variant price for a product (null if none priced). */
export const getProductMinPrice = (
  product: HttpTypes.StoreProduct
): number | null => {
  const prices = (product.variants || [])
    .map((v) => v.calculated_price?.calculated_amount)
    .filter((n): n is number => typeof n === "number")
  return prices.length ? Math.min(...prices) : null
}

/** Build the Size + price facets from the full (unfiltered) collection set. */
export const deriveCollectionFacets = (
  products: HttpTypes.StoreProduct[]
): CollectionFacets => {
  const sizeSet = new Set<string>()
  let priceMin = Infinity
  let priceMax = -Infinity

  for (const product of products) {
    for (const size of getProductSizeValues(product)) sizeSet.add(size)
    const min = getProductMinPrice(product)
    if (min !== null) {
      if (min < priceMin) priceMin = min
      if (min > priceMax) priceMax = min
    }
  }

  return {
    sizes: Array.from(sizeSet).sort(compareSizes),
    priceMin: Number.isFinite(priceMin) ? priceMin : 0,
    priceMax: Number.isFinite(priceMax) ? priceMax : 0,
  }
}

/** Apply the selected Size + price-range filters to the collection set. */
export const filterCollectionProducts = (
  products: HttpTypes.StoreProduct[],
  filters: CollectionFilters
): HttpTypes.StoreProduct[] => {
  // `filters.sizes` are URL slugs; match against slugified product sizes.
  const selectedSlugs = new Set(filters.sizes.map(sizeToParam))
  const { minPrice, maxPrice } = filters

  if (!selectedSlugs.size && minPrice === undefined && maxPrice === undefined) {
    return products
  }

  return products.filter((product) => {
    if (selectedSlugs.size) {
      const productSlugs = getProductSizeValues(product).map(sizeToParam)
      if (!productSlugs.some((s) => selectedSlugs.has(s))) return false
    }

    if (minPrice !== undefined || maxPrice !== undefined) {
      const price = getProductMinPrice(product)
      if (price === null) return false
      if (minPrice !== undefined && price < minPrice) return false
      if (maxPrice !== undefined && price > maxPrice) return false
    }

    return true
  })
}

/**
 * Parse a comma-joined `size` query param into clean slugs. Slugifying here
 * means a directly-typed raw URL (`?size=2/3`) still lights up the matching pill
 * (sizeToParam is idempotent, so our own slug links pass through unchanged).
 */
export const parseSizesParam = (raw?: string): string[] =>
  (raw || "")
    .split(",")
    .map((s) => sizeToParam(s))
    .filter(Boolean)

/** Parse a numeric price query param, ignoring blanks / NaN. */
export const parsePriceParam = (raw?: string): number | undefined => {
  if (raw === undefined || raw === "") return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}
